import { Box, Divider, Paper, Skeleton, ThemeProvider } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState } from "react";
import Subheading from "../../components/common/Subheading";
import Title from "../../components/common/Title";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import { LOG_STREAM_THEME } from "../../theme";
import FloatingActionButtons from "./components/FloatingActionButtons";
import LogLevelFilterPanel from "./components/LogLevelFilterPanel";
import RefreshButton from "./components/RefreshButton";
import TaskMetadata from "./components/TaskMetadata";
import { ALL_LOG_LEVELS } from "./constants";
import useLogStreamData from "./hooks/useLogStreamData";

/**
 * LogStreamPage component displays log stream data with filtering capabilities.
 *
 * Features:
 * - Displays log stream entries with filterable log levels
 * - Shows error and warning counts
 * - Displays metadata about the scraper task
 * - Loading state with skeleton placeholders
 * - Manual refresh functionality
 * - Floating action buttons for quick actions
 *
 * @returns {JSX.Element} Rendered LogStreamPage component
 */
function LogStreamPage() {
  const [activeLogLevels, setActiveLogLevels] = useState(ALL_LOG_LEVELS);
  const { logStreamName, errorCount, warningCount, logStreamElements, logStreamMongoData, taskFinished, syncLogs } =
    useLogStreamData();

  const displayLogStreamElems = () => {
    // Show loading skeletons when no data is available yet and task is still running
    if (logStreamElements.length === 0 && !taskFinished) {
      // Create array of skeleton placeholder items
      return Array.from({ length: 10 }, (_, index) => (
        <Skeleton key={`skeleton-${index}`} variant="text" width="100%" sx={{ bgcolor: "#4a4d50" }} />
      ));
    }

    // Return log elements filtered by currently active log levels
    return logStreamElements.filter((element) =>
      activeLogLevels.some((level) => element.props.loglevel.includes(level))
    );
  };

  const toggleLogLevelFilter = (logLevel) => {
    setActiveLogLevels((prevActiveLevels) => {
      const isCurrentlyActive = prevActiveLevels.includes(logLevel);

      if (isCurrentlyActive) {
        // Remove the log level if it's already active
        return prevActiveLevels.filter((level) => level !== logLevel);
      } else {
        // Add the log level if it's not active
        return [...prevActiveLevels, logLevel];
      }
    });
  };

  return (
    <Layout>
      {/* Top Bar */}
      <TopBarGridContainer sx={{ px: 15, pt: 10 }}>
        {/* Title Area */}
        <Grid size="grow">
          <Title text={"Log Stream"} />
          <Subheading text={logStreamName} sx={{ mb: 2 }} />
        </Grid>

        {/* Controls */}
        <Grid size="grow">
          {/* Refresh Button */}
          <RefreshButton onRefresh={() => syncLogs()} />
          {/* Filters */}
          <LogLevelFilterPanel activeLogLevels={activeLogLevels} onFilterChange={toggleLogLevelFilter} />
        </Grid>
      </TopBarGridContainer>

      {/* Log Stream Content */}
      <Box sx={{ mx: 15 }}>
        <Paper variant={"outlined"} sx={{ py: 10, px: 10, backgroundColor: "#1e2124" }}>
          <TaskMetadata
            taskFinished={taskFinished}
            errorCount={errorCount}
            warningCount={warningCount}
            logStreamMongoData={logStreamMongoData}
          />
          <Divider sx={{ my: 5 }} />

          {/* Log Entries */}
          <ThemeProvider theme={LOG_STREAM_THEME}>{displayLogStreamElems()}</ThemeProvider>
        </Paper>
      </Box>

      {/* Floating Options */}
      <FloatingActionButtons refreshFunction={syncLogs} />
    </Layout>
  );
}

export default LogStreamPage;
